import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { useAuth } from './AuthContext';
import { CpaConnection, CpaSettings } from '../types/cpa';

type CpaContextValue = {
  loading: boolean;
  connections: CpaConnection[];
  rivhitDocsEnabled: boolean;
  docsSentIds: string[];
  reload: () => Promise<void>;
  save: (payload: Record<string, unknown>) => Promise<{ success: boolean; error?: string }>;
};

const CpaContext = createContext<CpaContextValue | undefined>(undefined);

const emptySettings = (): Partial<CpaSettings> => ({
  connections: [],
  docsSentIds: [],
  rivhitDocsEnabled: false,
});

export const CpaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [settings, setSettings] = useState<Partial<CpaSettings>>(emptySettings());
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const settingsRes = await api.expenses.getCpaSettings();
      if (settingsRes.success && settingsRes.data) {
        setSettings(settingsRes.data as CpaSettings);
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    reload();
  }, [reload]);

  const save = useCallback(async (payload: Record<string, unknown>) => {
    const response = await api.expenses.putCpaSettings(payload);
    if (response.success && response.data) {
      setSettings(response.data as CpaSettings);
      return { success: true };
    }
    return { success: false, error: response.error };
  }, []);

  const value = useMemo<CpaContextValue>(
    () => ({
      loading,
      connections: (settings.connections || []) as CpaConnection[],
      rivhitDocsEnabled: Boolean(settings.rivhitDocsEnabled),
      docsSentIds: settings.docsSentIds || [],
      reload,
      save,
    }),
    [loading, settings, reload, save]
  );

  return <CpaContext.Provider value={value}>{children}</CpaContext.Provider>;
};

export const useCpa = () => {
  const ctx = useContext(CpaContext);
  if (!ctx) {
    throw new Error('useCpa must be used within a CpaProvider');
  }
  return ctx;
};
